import moment from 'moment';

/**
 * Calculate time remaining until PIN expiration
 * @param {string} pinExpiration - UTC timestamp string
 * @returns {Object|null} { hoursRemaining, daysRemaining, isExpired } or null if invalid
 */
export const getTimeUntilExpiration = (pinExpiration) => {
  if (!pinExpiration) return null;

  const expiration = moment(pinExpiration);
  if (!expiration.isValid()) return null;

  const now = moment();
  const hoursRemaining = Math.floor(expiration.diff(now, 'hours', true));
  const daysRemaining = Math.floor(hoursRemaining / 24);

  return {
    hoursRemaining: Math.max(0, hoursRemaining),
    daysRemaining: Math.max(0, daysRemaining),
    isExpired: expiration.isSameOrBefore(now),
  };
};

/**
 * Determine PIN expiration status
 * @param {string} pinExpiration - UTC timestamp string
 * @returns {'expired'|'required'|'optional'|'valid'}
 */
export const getExpirationStatus = (pinExpiration) => {
  const timeInfo = getTimeUntilExpiration(pinExpiration);
  if (!timeInfo) return 'valid';

  const { hoursRemaining, daysRemaining, isExpired } = timeInfo;

  if (isExpired) return 'expired';
  if (hoursRemaining <= 72) return 'required';
  if (daysRemaining <= 7) return 'optional';
  return 'valid';
};

/**
 * Get user-friendly expiration message
 * @param {string} pinExpiration - UTC timestamp string
 * @param {'required'|'optional'} status - Expiration status
 * @param {Function} t - i18next translation function
 * @returns {string} Formatted message
 */
export const getExpirationMessage = (pinExpiration, status, t) => {
  const timeInfo = getTimeUntilExpiration(pinExpiration);
  if (!timeInfo) return '';

  const { hoursRemaining, daysRemaining } = timeInfo;

  if (status === 'required') {
    if (hoursRemaining < 24) {
      return t('Your PIN expires in {{count}} hours and must be changed now.', {
        count: hoursRemaining,
      });
    }
    return t('Your PIN expires in {{count}} days and must be changed now.', {
      count: Math.ceil(hoursRemaining / 24),
    });
  }
  return t(
    'Your PIN expires in {{count}} day. Would you like to change it now?',
    {
      count: daysRemaining,
      defaultValue_plural:
        'Your PIN expires in {{count}} days. Would you like to change it now?',
    }
  );
};
